import { get } from 'lodash';

import { UPSERT_MERCHANT_REG_DETAILS_URL } from 'constants/url';
import {
  GET_LIFETIME_GMV_URL,
  GET_RISKSHIELD_DATA_URL,
} from 'containers/Summary/constants';

import {
  GET_DISPUTE_DETAILS_URL,
  GET_FIRST_PG_TRANSACTION_DATE_URL,
  GET_RISKSHIELD_ACTIVE_STATUS_URL,
} from 'containers/Disputes/constants';

import {
  downloadFromUrl,
  logout,
  getRequestHeaders,
  getRequestUrlAndToken,
  sentryCaptureException,
  useQuery,
} from './common';
import {
  PUBLIC_URLS,
  UPDATE_MODES_LIMIT_URL,
  TRACK_EVENT,
  HC_FORM,
} from './constants';

import CustomError from './customError';

import Emitter from './emitter';

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */

export function appendQueryString(obj) {
  return Object.keys(obj).reduce((acc, curr) => {
    const val = encodeURIComponent(obj[curr]);
    return acc.length === 0 ? `?${curr}=${val}` : `${acc}&${curr}=${val}`;
  }, '');
}

function formatErrorMessage(message = '') {
  const formattedMessage = message
    .toUpperCase()
    .trim()
    .replace(/ /g, '_');
  return formattedMessage === 'INTERNAL_SERVER_ERROR'
    ? 'Something went wrong. Please retry after some time.'
    : message;
}

function downloadBlob(response) {
  return new Promise((resolve, reject) => {
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = '';
    if (contentDisposition.indexOf('filename') > -1) {
      const resultant = contentDisposition.split('=');
      filename = resultant[resultant.length - 1];
    }
    response
      .blob()
      .then(blob => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        downloadFromUrl(url, filename);
        resolve();
      })
      .catch(e => reject(e));
  });
}

function trackAPITurnAroundTime(obj) {
  Emitter.emit(TRACK_EVENT, obj);
}

export function makeFetchRequest(fetchUrl, requestOptions) {
  return new Promise((resolve, reject) => {
    let timeOut = 20;
    const { connectionTimeout, track, ...restOptions } = requestOptions;
    if (connectionTimeout) timeOut = connectionTimeout;

    let isPromiseSettled = false;

    const t0 = Date.now();

    fetch(fetchUrl, restOptions)
      .then(response => {
        isPromiseSettled = true;
        if (track)
          trackAPITurnAroundTime({
            url: fetchUrl,
            duration: Date.now() - t0,
            timeout: false,
            error: !response.ok,
          });

        const contentType = response.headers.get('Content-Type');
        if (contentType.indexOf('application/json') > -1) {
          return response
            .json()
            .then(data => Object.assign({ statusCode: response.status }, data));
        }
        return downloadBlob(response);
      })
      .then(data => resolve(data))
      .catch(() => {
        isPromiseSettled = true;
        if (track)
          trackAPITurnAroundTime({
            url: fetchUrl,
            duration: Date.now() - t0,
            timeout: false,
            error: true,
          });
        reject(new Error('Unable to proceed. Please retry after some time.'));
      });

    setTimeout(() => {
      if (!isPromiseSettled && track)
        trackAPITurnAroundTime({
          url: fetchUrl,
          duration: Date.now() - t0,
          timeout: true,
          error: false,
        });
      // sentryCaptureException({ errMsg: `Request Timeout - ${fetchUrl}` });
      reject(new Error('Unable to proceed. Please retry after some time.'));
    }, timeOut * 1000);
  });
}

const RETURN_DIRECT_RESPONSE_URLS = [
  HC_FORM.url,
  GET_RISKSHIELD_ACTIVE_STATUS_URL.url,
  GET_RISKSHIELD_DATA_URL.url,
  GET_LIFETIME_GMV_URL.url,
  GET_DISPUTE_DETAILS_URL.url,
  GET_FIRST_PG_TRANSACTION_DATE_URL.url,
];

export default async function request(
  url,
  options = {},
  substituteBaseUrl = '',
  customBasePath = '',
) {
  const {
    envCall,
    queryString = {},
    isModuleFederated,
    useBasePath = false,
    disableSentryLogging = false, // If true, it would skip the 5xx error logging to sentry
    skipLogout, // To skip logout when error message is UNAUTHORIZED
    removeHeaders,
    directResponseReturn,
    isImportMerchant,
    ...requestOptions
  } = options;

  requestOptions.headers = await getRequestHeaders(requestOptions);

  const { baseUrl, token } = getRequestUrlAndToken(
    envCall,
    useBasePath,
    isModuleFederated,
    customBasePath,
    isImportMerchant,
  );

  // disabling eslint warning as this useQuery is not a hook by implimentation but named as hook only
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const type = useQuery().get('type');

  if (!PUBLIC_URLS.includes(url) && !requestOptions.headers.Authorization)
    requestOptions.headers.Authorization = `Bearer ${
      options.token ? options.token : token
    }`;

  const fetchUrl = `${substituteBaseUrl || baseUrl}${
    type ? `/${type}` : ''
  }${url}${
    Object.keys(queryString).length > 0 ? appendQueryString(queryString) : ''
  }`;
  if (removeHeaders) {
    requestOptions.headers = {};
  }
  const response = await makeFetchRequest(fetchUrl, requestOptions);
  if (
    response.statusCode === 200 &&
    (RETURN_DIRECT_RESPONSE_URLS.includes(url) || directResponseReturn)
  ) {
    return response;
  }

  if (
    (response.status &&
      typeof response.status === 'string' &&
      (response.status?.toUpperCase() === 'SUCCESS' ||
        response.status?.toUpperCase() === 'OK')) ||
    ['EMPTY_LIST', 'STATUS_OK'].includes(response.code)
  ) {
    return response.data || response;
  }

  if (response.baasAccounts) return response;

  if (
    get(response, 'message', '')?.toUpperCase() === 'UNAUTHORIZED' &&
    !skipLogout
  ) {
    /**
     * When user visits authenticated page with "merchantToken" present in localStorage and if "merchantToken" is invalid/expired then
     * @function logout will execute and user would be redirected to login page.
     *
     * Before this redirection we must store the current page url in sessionStorage variable named "referrerUrl".
     *
     * This `referrerUrl` will be used post successful Login to
     * redirect user back to original page.
     */
    sessionStorage.setItem('referrerUrl', window.location.href);
    logout();
  }

  if (!disableSentryLogging && response?.statusCode >= 500)
    sentryCaptureException({
      errMsg: response?.message,
      fetchUrl,
      priority: 'p0',
      statusCode: response?.statusCode,
      errorType: 'api',
      apiEndpoint: fetchUrl,
    });
  throw new CustomError(
    response,
    formatErrorMessage(response?.message || response?.detail || ''),
  );
}

export function buildFormDataObject({ data, fieldFormatMapper }) {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    const docType = get(fieldFormatMapper, `${key}.name`, '');
    formData.append('file', data[key].file, data[key].fileName);
    formData.append('docType', docType);
  });
  return formData;
}

export function formatRequestBody({ data, fieldFormatMapper }) {
  let payload = [];
  const regDetails = [];

  Object.keys(data).forEach(key => {
    if (data[key] !== undefined) {
      const mapperObj = get(fieldFormatMapper, key, []);
      mapperObj.forEach(urlObj => {
        if (urlObj.url === UPSERT_MERCHANT_REG_DETAILS_URL.url) {
          regDetails.push({
            regKey: key,
            regVal: data[key],
          });
        } else if (urlObj.url === UPDATE_MODES_LIMIT_URL.url) {
          payload.push({
            type: key,
            value: data[key].value,
            status: 'ACTIVE',
            url: urlObj,
            options: {
              roleId: data[key].roleId,
            },
          });
        } else {
          payload.push({});
          payload[payload.length - 1][key] = data[key];
          payload[payload.length - 1].url = urlObj;
        }
      });
    }
  });
  if (regDetails.length > 0) {
    payload = [
      ...payload,
      { regDetails, url: UPSERT_MERCHANT_REG_DETAILS_URL },
    ];
  }
  return payload;
}
